/**
 * Demandes de financement déposées par l'organisme (04/11/2026) : tableau de l'organisme, suivi d'une demande, fiches
 * financeurs (Organisme → Financeurs), carte « Financement » de la fiche dossier.
 * Aucune API des financeurs : on prépare, on suit, on recopie ; rien ne part sans confirmation.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Banknote,
  CalendarClock,
  Check,
  ExternalLink,
  FileDown,
  FileSignature,
  Inbox,
  Mail,
  Printer,
  Send,
} from "lucide-react";
import {
  api,
  dateFr,
  euros,
  instantFr,
  type DemandeFinancement,
  type DemandesDuDossier,
  type FichesFinanceurs,
  type ListeDemandesFinancement,
} from "../api";
import {
  Alerte,
  Bouton,
  Carte,
  Champ,
  Chargement,
  cx,
  DepotFichier,
  EtatVide,
  Etiquette,
  Indicateur,
  Modale,
  Selecteur,
  TitrePage,
  useNotifier,
  useTitreDocument,
} from "../ui/base";

const LIBELLES_CANAL: Record<string, string> = {
  portail: "Portail du financeur",
  email: "E-mail à l'interlocuteur",
  formulaire: "Formulaire PDF signé à la main",
};
const centimes = (texte: string): number | null =>
  texte.trim() === "" ? null : Math.round(Number(texte.replace(",", ".").replace(/\s/g, "")) * 100);

// ——— Tableau de l'organisme ———

export function TableauFinancements() {
  useTitreDocument("Demandes de financement");
  const [financeur, setFinanceur] = useState("");
  const [statut, setStatut] = useState("");
  const [vue, setVue] = useState("");
  const q = new URLSearchParams(Object.entries({ financeur, statut, vue }).filter(([, v]) => v)).toString();
  const liste = useQuery({
    queryKey: ["financements", q],
    queryFn: () => api.get<ListeDemandesFinancement>(`/admin/financements${q ? `?${q}` : ""}`),
  });
  return (
    <>
      <TitrePage
        titre="Demandes de financement"
        soustitre="Les demandes que l'organisme dépose pour ses clients (CPF, FAFCEA, FIFPL, OPCO). Retards et échéances proches en tête. Service inclus : aucune facturation."
        actions={
          <Link
            to="/admin/financeurs"
            className="text-sm font-medium text-accent-fort underline underline-offset-2"
          >
            Fiches financeurs
          </Link>
        }
      />
      {liste.isPending ? (
        <Chargement />
      ) : liste.error ? (
        <Alerte ton="danger">{liste.error.message}</Alerte>
      ) : (
        <>
          {liste.data.actifs.some((a) => a.financeur.startsWith("cpf")) && (
            <section aria-label="Tableau de bord CPF" className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Indicateur
                libelle="CPF à traiter"
                valeur={liste.data.compteurs_cpf.a_traiter}
                ton={liste.data.compteurs_cpf.a_traiter ? "attente" : "neutre"}
                icone={<Inbox className="size-4" aria-hidden />}
                onClick={() => setVue("en_cours")}
              />
              <Indicateur
                libelle="Validation du titulaire"
                valeur={liste.data.compteurs_cpf.validation_titulaire}
                icone={<CalendarClock className="size-4" aria-hidden />}
                onClick={() => setStatut("en_attente")}
              />
              <Indicateur
                libelle="Service fait à déclarer"
                valeur={liste.data.compteurs_cpf.service_fait_a_declarer}
                ton={liste.data.compteurs_cpf.service_fait_a_declarer ? "danger" : "neutre"}
                icone={<AlertTriangle className="size-4" aria-hidden />}
                onClick={() => setVue("en_cours")}
              />
              <Indicateur
                libelle="Dossiers à facturer"
                valeur={liste.data.compteurs_cpf.a_facturer}
                icone={<Banknote className="size-4" aria-hidden />}
                onClick={() => setVue("a_facturer")}
              />
            </section>
          )}
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <Selecteur
              libelle="Financeur"
              value={financeur}
              onChange={(e) => setFinanceur(e.target.value)}
              className="w-56"
            >
              <option value="">Tous</option>
              <option value="cpf">CPF (formation et bilan)</option>
              {liste.data.actifs.map((a) => (
                <option key={a.financeur} value={a.financeur}>
                  {a.libelle}
                </option>
              ))}
            </Selecteur>
            <Selecteur
              libelle="Statut"
              value={statut}
              onChange={(e) => setStatut(e.target.value)}
              className="w-64"
            >
              <option value="">Tous</option>
              {liste.data.statuts.map((x) => (
                <option key={x.valeur} value={x.valeur}>
                  {x.libelle}
                </option>
              ))}
            </Selecteur>
            <div role="group" aria-label="Vues EDOF" className="flex flex-wrap gap-1.5">
              {liste.data.vues_cpf.map((v) => (
                <button
                  key={v.cle}
                  type="button"
                  aria-pressed={vue === v.cle || (!vue && v.cle === "tous")}
                  onClick={() => setVue(v.cle === "tous" ? "" : v.cle)}
                  className={cx(
                    "min-h-9 rounded-full border px-3 text-[13px]",
                    vue === v.cle || (!vue && v.cle === "tous")
                      ? "border-accent bg-accent-doux font-semibold text-accent-fort"
                      : "border-trait bg-carte text-encre-2 hover:bg-papier-3",
                  )}
                >
                  CPF · {v.libelle} <span className="chiffres text-encre-3">{v.nombre}</span>
                </button>
              ))}
            </div>
          </div>
          {liste.data.lignes.length === 0 ? (
            <EtatVide icone={<Banknote className="size-8" aria-hidden />} titre="Aucune demande">
              Ouvrez une demande depuis la fiche d'un dossier (carte « Financement »).
            </EtatVide>
          ) : (
            <ul className="space-y-2.5">
              {liste.data.lignes.map((l) => (
                <li key={l.id}>
                  <Link
                    to="/financements/$id"
                    params={{ id: l.id }}
                    className="block rounded-lg border border-trait bg-carte p-4 hover:border-accent/50 focus-visible:outline-offset-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Etiquette ton="accent">{l.libelle_financeur}</Etiquette>
                      <Etiquette
                        ton={
                          l.statut === "refuse" || l.statut === "annule"
                            ? "danger"
                            : l.statut === "accorde" || l.statut === "facture"
                              ? "accent"
                              : "attente"
                        }
                      >
                        {l.libelle_statut}
                      </Etiquette>
                      {l.alertes.map((a) => (
                        <Etiquette key={a.libelle} ton={a.niveau === "retard" ? "danger" : "attente"}>
                          {a.libelle}
                        </Etiquette>
                      ))}
                    </div>
                    <div className="mt-2 grid gap-1 text-sm sm:grid-cols-[1fr_auto]">
                      <span className="min-w-0">
                        <span className="font-semibold">{l.reference}</span> ·{" "}
                        <span className="text-encre-2">{l.formation}</span>
                        {l.numero_portail && (
                          <span className="chiffres text-encre-3"> · n° {l.numero_portail}</span>
                        )}
                      </span>
                      <span className="text-encre-2 sm:text-right">
                        {l.prochaine_echeance ? (
                          <>
                            {l.prochaine_echeance.libelle} :{" "}
                            <strong className="chiffres">{dateFr(l.prochaine_echeance.date)}</strong>
                          </>
                        ) : l.montant_accorde != null ? (
                          <>Accordé : {euros(l.montant_accorde)}</>
                        ) : null}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}

// ——— Carte de la fiche dossier ———

export function CarteFinancementsDossier({ dossierId, admin }: { dossierId: string; admin: boolean }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const [choix, setChoix] = useState("");
  const q = useQuery({
    queryKey: ["financements-dossier", dossierId],
    queryFn: () => api.get<DemandesDuDossier>(`/dossiers/${dossierId}/financements`),
  });
  const ouvrir = useMutation({
    mutationFn: () =>
      api.post<DemandeFinancement>(`/dossiers/${dossierId}/financements`, { financeur: choix }),
    onSuccess: async (r) => {
      await requetes.invalidateQueries({ queryKey: ["financements-dossier", dossierId] });
      await requetes.invalidateQueries({ queryKey: ["dossier", dossierId] });
      notifier(
        "succes",
        `Demande ${r.libelle} ouverte${r.mandat ? " — mandat généré, à faire signer par le client" : ""}.`,
      );
    },
    onError: (e) => notifier("danger", e.message),
  });
  if (!q.data || (q.data.actifs.length === 0 && q.data.demandes.length === 0)) return null;
  const libres = q.data.actifs.filter((a) => !q.data.demandes.some((d) => d.financeur === a.financeur));
  return (
    <Carte className="p-4 sm:p-5">
      <h2 className="text-[15px] font-semibold">Demandes de financement</h2>
      {q.data.demandes.length === 0 ? (
        <p className="mt-1 text-sm text-encre-2">
          Aucune demande ouverte.{" "}
          {admin
            ? "L'organisme dépose la demande pour le client."
            : "L'organisme la dépose pour votre client."}
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {q.data.demandes.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
              <Etiquette ton="accent">{d.libelle}</Etiquette>
              <span>{d.libelle_statut}</span>
              {d.montant_accorde != null && (
                <span className="chiffres text-encre-2">· accordé {euros(d.montant_accorde)}</span>
              )}
              {admin && (
                <Link
                  to="/financements/$id"
                  params={{ id: d.id }}
                  className="font-medium text-accent-fort underline underline-offset-2"
                >
                  Suivre
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
      {admin && libres.length > 0 && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <Selecteur
            libelle="Financeur"
            value={choix}
            onChange={(e) => setChoix(e.target.value)}
            className="w-60"
          >
            <option value="">Choisir…</option>
            {libres.map((a) => (
              <option key={a.financeur} value={a.financeur}>
                {a.libelle}
              </option>
            ))}
          </Selecteur>
          <Bouton
            variante="signal"
            disabled={!choix}
            enCours={ouvrir.isPending}
            onClick={() => ouvrir.mutate()}
          >
            Ouvrir la demande
          </Bouton>
        </div>
      )}
    </Carte>
  );
}

// ——— Suivi d'une demande ———

const CHAMPS_PASSAGE: Record<string, Array<{ cle: string; libelle: string; type?: string }>> = {
  depose: [{ cle: "date", libelle: "Date de dépôt (réception par le financeur)", type: "date" }],
  accorde: [
    { cle: "date", libelle: "Date de l'accord", type: "date" },
    { cle: "montant_accorde", libelle: "Montant accordé (€ HT)" },
  ],
  refuse: [{ cle: "motif", libelle: "Motif du refus" }],
  annule: [{ cle: "motif", libelle: "Motif d'annulation" }],
  en_attente: [{ cle: "numero_portail", libelle: "N° de dossier EDOF (12 chiffres)" }],
  entree_declaree: [{ cle: "date_entree", libelle: "Date d'entrée effective", type: "date" }],
  sortie_declaree: [
    { cle: "date_sortie", libelle: "Date de sortie effective", type: "date" },
    { cle: "motif_sortie", libelle: "Motif de sortie" },
  ],
  service_fait_declare: [{ cle: "taux_realisation", libelle: "Taux de réalisation (%)" }],
  facture: [
    { cle: "numero_facture", libelle: "N° de facture" },
    { cle: "date_facture", libelle: "Date d'émission", type: "date" },
  ],
};

export function SuiviDemande() {
  const { id } = useParams({ strict: false }) as { id: string };
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({
    queryKey: ["financement", id],
    queryFn: () => api.get<DemandeFinancement>(`/financements/${id}`),
  });
  useTitreDocument(
    q.data ? `Financement ${q.data.libelle} — ${q.data.dossier.reference}` : "Demande de financement",
  );
  const [vers, setVers] = useState("");
  const [valeurs, setValeurs] = useState<Record<string, string>>({});
  const [courriel, setCourriel] = useState(false);
  const rafraichir = async (r?: DemandeFinancement) => {
    if (r) requetes.setQueryData(["financement", id], r);
    await requetes.invalidateQueries({ queryKey: ["financements"] });
  };
  const passer = useMutation({
    mutationFn: () => {
      const v: Record<string, unknown> = { statut: vers };
      for (const [k, x] of Object.entries(valeurs))
        if (x !== "")
          v[k] =
            k === "montant_accorde"
              ? centimes(x)
              : k === "taux_realisation"
                ? Number(x.replace(",", "."))
                : x;
      return api.post<DemandeFinancement>(`/financements/${id}/statut`, v);
    },
    onSuccess: async (r) => {
      await rafraichir(r);
      setVers("");
      setValeurs({});
      notifier("succes", `Statut : ${r.libelle_statut}.`);
    },
    onError: (e) => notifier("danger", e.message),
  });
  const infos = useMutation({
    mutationFn: (corps: Record<string, unknown>) =>
      api.patch<DemandeFinancement>(`/financements/${id}`, corps),
    onSuccess: async (r) => (await rafraichir(r), notifier("succes", "Enregistré.")),
    onError: (e) => notifier("danger", e.message),
  });
  const relance = useMutation({
    mutationFn: () => api.post<DemandeFinancement>(`/financements/${id}/relance`, {}),
    onSuccess: async (r) => (await rafraichir(r), notifier("succes", "Relance notée dans le journal.")),
    onError: (e) => notifier("danger", e.message),
  });
  const scan = useMutation({
    mutationFn: (f: File) => api.fichier<DemandeFinancement>(`/financements/${id}/formulaire-signe`, f),
    onSuccess: async (r) => (await rafraichir(r), notifier("succes", "Formulaire signé enregistré.")),
    onError: (e) => notifier("danger", e.message),
  });

  if (q.isPending) return <Chargement />;
  if (q.error) return <Alerte ton="danger">{q.error.message}</Alerte>;
  const r = q.data;
  const dm = r.demande;
  const prochains = r.statuts.filter((x) => x.valeur !== dm.statut);
  return (
    <>
      <TitrePage
        titre={`${r.libelle} — ${r.dossier.reference}`}
        soustitre={
          <>
            {r.dossier.titre} · du {dateFr(r.dossier.debut)} au {dateFr(r.dossier.fin)} ·{" "}
            {r.dossier.stagiaires.join(", ")} ·{" "}
            <Link to="/dossiers/$id" params={{ id: r.dossier.id }} className="underline underline-offset-2">
              ouvrir le dossier
            </Link>
          </>
        }
        actions={
          <a
            href={`/api/financements/${id}/recapitulatif`}
            target="_blank"
            rel="noopener"
            className="inline-flex min-h-10 items-center gap-2 rounded-md border border-trait bg-carte px-3 text-sm font-medium hover:bg-papier-3"
          >
            <Printer className="size-4" aria-hidden /> Récapitulatif imprimable
          </a>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Etiquette ton="accent">{r.libelle_statut}</Etiquette>
        <Etiquette>{LIBELLES_CANAL[dm.canal] ?? dm.canal}</Etiquette>
        {dm.numero_portail && <Etiquette>n° {dm.numero_portail}</Etiquette>}
        {dm.lien_portail && (
          <a
            href={dm.lien_portail}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-accent-fort underline underline-offset-2"
          >
            {r.edof ? "Ouvrir dans EDOF" : "Ouvrir sur le portail"}{" "}
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
        )}
      </div>
      {r.alertes.length > 0 && (
        <div className="mb-4 space-y-2">
          {r.alertes.map((a) => (
            <Alerte key={a.libelle} ton={a.niveau === "retard" ? "danger" : "attention"}>
              {a.libelle} ({dateFr(a.date)})
            </Alerte>
          ))}
        </div>
      )}
      {r.formulaire?.perime && (
        <div className="mb-4">
          <Alerte ton="attention" titre="Vérifiez le formulaire de l'année en cours">
            La version utilisée date de {r.formulaire.millesime}. Téléversez le formulaire de l'année dans les
            fiches financeurs.
          </Alerte>
        </div>
      )}
      {r.ecart_dates_session && (
        <div className="mb-4">
          <Alerte ton="danger" titre="Dates du dossier différentes des dates de session EDOF">
            Les pièces (convention, convocation) se fondent sur les dates du dossier : alignez-les sur la
            session EDOF.
          </Alerte>
        </div>
      )}
      {r.engagement_bilan && !r.engagement_bilan.signe && (
        <div className="mb-4">
          <Alerte ton="danger" titre="Engagement de confidentialité non signé">
            Le consultant puis le bénéficiaire signent dans leurs espaces ; l'entrée en formation ne peut pas
            être déclarée avant.
          </Alerte>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-4">
          <Carte className="p-4 sm:p-5">
            <h2 className="text-[15px] font-semibold">Faire avancer la demande</h2>
            <p className="mt-1 text-[13px] text-encre-2">
              Statut relevé sur le portail ou reçu du financeur, recopié ici. Chaque changement est daté et
              journalisé (Qualiopi).
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Selecteur
                libelle="Nouveau statut"
                value={vers}
                onChange={(e) => (setVers(e.target.value), setValeurs({}))}
              >
                <option value="">Choisir…</option>
                {prochains.map((x) => (
                  <option key={x.valeur} value={x.valeur}>
                    {x.libelle}
                  </option>
                ))}
              </Selecteur>
              {(CHAMPS_PASSAGE[vers] ?? []).map((c) =>
                c.cle === "motif" && vers === "annule" && r.famille === "cpf" ? (
                  <Selecteur
                    key={c.cle}
                    libelle={c.libelle}
                    value={valeurs[c.cle] ?? ""}
                    onChange={(e) => setValeurs({ ...valeurs, [c.cle]: e.target.value })}
                  >
                    <option value="">Choisir…</option>
                    {r.motifs.annulation.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </Selecteur>
                ) : c.cle === "motif_sortie" ? (
                  <Selecteur
                    key={c.cle}
                    libelle={c.libelle}
                    value={valeurs[c.cle] ?? ""}
                    onChange={(e) => setValeurs({ ...valeurs, [c.cle]: e.target.value })}
                  >
                    <option value="">Choisir…</option>
                    {r.motifs.sortie.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </Selecteur>
                ) : (
                  <Champ
                    key={c.cle}
                    libelle={c.libelle}
                    type={c.type ?? "text"}
                    value={valeurs[c.cle] ?? ""}
                    onChange={(e) => setValeurs({ ...valeurs, [c.cle]: e.target.value })}
                  />
                ),
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Bouton
                variante="signal"
                disabled={!vers}
                enCours={passer.isPending}
                onClick={() => passer.mutate()}
              >
                Enregistrer le statut
              </Bouton>
              {(dm.statut === "depose" || dm.statut === "attente_pieces") && (
                <Bouton variante="secondaire" enCours={relance.isPending} onClick={() => relance.mutate()}>
                  Noter une relance
                </Bouton>
              )}
            </div>
          </Carte>

          {dm.canal === "formulaire" && (
            <Carte className="p-4 sm:p-5">
              <h2 className="flex items-center gap-2 text-[15px] font-semibold">
                <FileSignature className="size-4" aria-hidden /> Formulaire du financeur — signature
                manuscrite
              </h2>
              <p className="mt-1 text-[13px] text-encre-2">
                {r.formulaire
                  ? `Formulaire ${r.formulaire.millesime}${r.formulaire.version ? ` (${r.formulaire.version})` : ""}, ${r.formulaire.nb_champs} champ(s).`
                  : "Aucun formulaire téléversé : ajoutez celui de l'année dans les fiches financeurs."}{" "}
                Pré-remplissez-le, faites-le signer à la main par le stagiaire, puis téléversez le scan.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {r.formulaire && r.formulaire.nb_champs > 0 && (
                  <a
                    href={`/api/financements/${id}/formulaire-pre-rempli`}
                    className="inline-flex min-h-10 items-center gap-2 rounded-md border border-trait bg-carte px-3 text-sm font-medium hover:bg-papier-3"
                  >
                    <FileDown className="size-4" aria-hidden /> Formulaire pré-rempli (PDF)
                  </a>
                )}
                <a
                  href={`/api/financements/${id}/recopie`}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex min-h-10 items-center gap-2 rounded-md border border-trait bg-carte px-3 text-sm font-medium hover:bg-papier-3"
                >
                  <Printer className="size-4" aria-hidden /> Fiche de recopie
                </a>
                <DepotFichier
                  libelle={dm.formulaire_signe ? "Remplacer le scan signé" : "Téléverser le scan signé"}
                  accept=".pdf,.png,.jpg,.jpeg"
                  enCours={scan.isPending}
                  deposer={(f) => scan.mutate(f)}
                />
              </div>
              {dm.formulaire_signe && (
                <p className="mt-2 flex items-center gap-1.5 text-sm text-valide">
                  <Check className="size-4" aria-hidden /> Formulaire signé reçu le{" "}
                  {instantFr(dm.formulaire_signe_le)} —{" "}
                  <a href={`/api/financements/${id}/formulaire-signe`} className="underline">
                    ouvrir
                  </a>
                </p>
              )}
            </Carte>
          )}

          {dm.canal === "email" && (
            <Carte className="p-4 sm:p-5">
              <h2 className="flex items-center gap-2 text-[15px] font-semibold">
                <Mail className="size-4" aria-hidden /> E-mail à l'interlocuteur
              </h2>
              <p className="mt-1 text-[13px] text-encre-2">
                Préparé à partir du dossier, envoyé par la messagerie de l'organisme, seulement après votre
                confirmation.{dm.email_envoye_le ? ` Dernier envoi : ${instantFr(dm.email_envoye_le)}.` : ""}
              </p>
              <Bouton
                className="mt-3"
                variante="secondaire"
                icone={<Send className="size-4" aria-hidden />}
                onClick={() => setCourriel(true)}
              >
                Préparer l'e-mail
              </Bouton>
              {courriel && <ApercuCourriel id={id} fermer={() => setCourriel(false)} />}
            </Carte>
          )}

          {r.edof && (
            <CarteCpf r={r} enregistrer={(c) => infos.mutate({ cpf: c })} enCours={infos.isPending} />
          )}
          {r.bilan && <CarteBilan r={r} enregistrer={(b) => infos.mutate({ bilan: b })} />}

          <Carte className="p-4 sm:p-5">
            <h2 className="text-[15px] font-semibold">Pièces exigées par le financeur</h2>
            <ul className="mt-2 divide-y divide-trait">
              {r.pieces.map((p) => (
                <li key={p.code} className="flex items-start gap-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 size-4"
                    aria-label={p.libelle}
                    checked={p.presente}
                    disabled={p.source === "dossier" || p.source === "formulaire signé" || infos.isPending}
                    onChange={(e) => {
                      const cochees = new Set((dm.pieces_cochees as string[]) ?? []);
                      if (e.target.checked) cochees.add(p.code);
                      else cochees.delete(p.code);
                      infos.mutate({ pieces_cochees: [...cochees] });
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    {p.libelle} {!p.obligatoire && <span className="text-encre-3">(selon le cas)</span>}
                    <span className="block text-[12px] text-encre-3">
                      {p.phase === "avant" ? "Avec la demande" : "Après la formation"}
                      {p.source ? ` · ${p.source === "dossier" ? "présente dans le dossier" : p.source}` : ""}
                    </span>
                  </span>
                  <Etiquette ton={p.presente ? "accent" : p.obligatoire ? "danger" : "neutre"}>
                    {p.presente ? "Présente" : "Manquante"}
                  </Etiquette>
                </li>
              ))}
            </ul>
            {r.mandat && (
              <p className={cx("mt-3 text-sm", r.mandat.signe ? "text-valide" : "text-danger-encre")}>
                Mandat du client :{" "}
                {r.mandat.signe
                  ? "signé"
                  : "à faire signer (espace du client, ou dépôt du mandat signé sur papier dans les pièces du dossier)"}{" "}
                — exigé avant tout dépôt.
              </p>
            )}
          </Carte>
        </div>

        <div className="space-y-4">
          <Carte className="p-4 sm:p-5">
            <h2 className="text-[15px] font-semibold">Échéances</h2>
            {r.echeances.length === 0 ? (
              <p className="mt-1 text-sm text-encre-2">
                Aucune échéance calculable (délais du financeur « à compléter » ou dates du dossier absentes).
              </p>
            ) : (
              <ul className="mt-2 space-y-1.5 text-sm">
                {r.echeances.map((x) => (
                  <li key={x.cle} className="flex justify-between gap-3">
                    <span>{x.libelle}</span>
                    <strong className="chiffres">{dateFr(x.date)}</strong>
                  </li>
                ))}
              </ul>
            )}
          </Carte>
          <CartePortail r={r} enregistrer={(c) => infos.mutate(c)} enCours={infos.isPending} />
          <Carte className="p-4 sm:p-5">
            <h2 className="text-[15px] font-semibold">Journal</h2>
            <ol className="mt-2 space-y-2 border-l-2 border-trait pl-3">
              {[...(dm.historique as Array<{ date: string; libelle: string; auteur: string; par?: string }>)]
                .reverse()
                .map((h, i) => (
                  <li key={i} className="text-sm">
                    <span className="chiffres text-[12px] text-encre-3">{instantFr(h.date)}</span> ·{" "}
                    <Etiquette>{h.auteur}</Etiquette>
                    <span className="block">{h.libelle}</span>
                    {h.par && <span className="text-[12px] text-encre-3">par {h.par}</span>}
                  </li>
                ))}
            </ol>
          </Carte>
          <Carte className="p-4 sm:p-5">
            <h2 className="text-[15px] font-semibold">Règles du financeur</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-encre-2">
              {r.config.regles.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            <p className="mt-2 text-[12px] text-encre-3">
              Sources :{" "}
              {r.config.sources.map((x, i) => (
                <a
                  key={x.url + i}
                  href={x.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {x.titre}
                  {i < r.config.sources.length - 1 ? " ; " : ""}
                </a>
              ))}
            </p>
          </Carte>
        </div>
      </div>
    </>
  );
}

function CartePortail({
  r,
  enregistrer,
  enCours,
}: {
  r: DemandeFinancement;
  enregistrer: (c: Record<string, unknown>) => void;
  enCours: boolean;
}) {
  const dm = r.demande;
  const [numero, setNumero] = useState(dm.numero_portail);
  const [lien, setLien] = useState(dm.lien_portail);
  const [statut, setStatut] = useState(dm.statut_portail);
  useEffect(
    () => (setNumero(dm.numero_portail), setLien(dm.lien_portail), setStatut(dm.statut_portail)),
    [dm.numero_portail, dm.lien_portail, dm.statut_portail],
  );
  return (
    <Carte className="p-4 sm:p-5">
      <h2 className="text-[15px] font-semibold">Sur le portail</h2>
      <p className="mt-1 text-[12px] text-encre-3">
        {dm.verification_portail_le
          ? `Dernière vérification : ${instantFr(dm.verification_portail_le)} (${dm.source_releve === "agent" ? "relevé par l'agent" : "saisi à la main"})`
          : "Jamais vérifié."}
      </p>
      <div className="mt-2 space-y-2">
        <Champ
          libelle={r.edof ? "N° de dossier EDOF (12 chiffres)" : "N° de dossier chez le financeur"}
          value={numero}
          onChange={(e) => setNumero(e.target.value)}
          inputMode={r.edof ? "numeric" : undefined}
        />
        <Champ
          libelle="Lien direct vers la fiche (https://…)"
          value={lien}
          onChange={(e) => setLien(e.target.value)}
        />
        <Champ
          libelle="Statut lu sur le portail"
          value={statut}
          onChange={(e) => setStatut(e.target.value)}
        />
      </div>
      <Bouton
        className="mt-3"
        variante="secondaire"
        taille="sm"
        enCours={enCours}
        onClick={() =>
          enregistrer({
            numero_portail: numero,
            lien_portail: lien,
            ...(statut !== dm.statut_portail ? { statut_portail: statut } : {}),
          })
        }
      >
        Enregistrer
      </Bouton>
    </Carte>
  );
}

function CarteCpf({
  r,
  enregistrer,
  enCours,
}: {
  r: DemandeFinancement;
  enregistrer: (c: Record<string, unknown>) => void;
  enCours: boolean;
}) {
  const cpf = r.demande.cpf as Record<string, unknown>;
  const champs = [
    ["edof_formation_id", "ID formation EDOF (ex. BDC_16)"],
    ["edof_action_id", "Action"],
    ["edof_session_id", "Session"],
    ["session_debut", "Session du", "date"],
    ["session_fin", "au", "date"],
    ["titulaire_nom_usage", "Titulaire — nom d'usage"],
    ["titulaire_nom_naissance", "Titulaire — nom de naissance"],
    ["documents_cloture_recus_le", "Documents de clôture reçus du formateur le", "date"],
  ] as const;
  const [v, setV] = useState<Record<string, string>>(
    Object.fromEntries(champs.map(([k]) => [k, String(cpf[k] ?? "")])),
  );
  return (
    <Carte className="p-4 sm:p-5">
      <h2 className="text-[15px] font-semibold">Dossier EDOF</h2>
      <p className="mt-1 text-[13px] text-encre-2">
        Toujours relié par le n° de dossier à 12 chiffres (la recherche par nom dans EDOF est peu fiable).
        Aucun montant de participation forfaitaire n'est calculé ici.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {champs.map(([k, l, t]) => (
          <Champ
            key={k}
            libelle={l}
            type={t ?? "text"}
            value={v[k] ?? ""}
            onChange={(e) => setV({ ...v, [k]: e.target.value })}
          />
        ))}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {cpf.date_entree ? (
          <>
            <dt className="text-encre-3">Entrée</dt>
            <dd>{dateFr(String(cpf.date_entree))}</dd>
          </>
        ) : null}
        {cpf.date_sortie ? (
          <>
            <dt className="text-encre-3">Sortie</dt>
            <dd>
              {dateFr(String(cpf.date_sortie))} — {String(cpf.motif_sortie ?? "")}
            </dd>
          </>
        ) : null}
        {cpf.taux_realisation != null ? (
          <>
            <dt className="text-encre-3">Taux de réalisation</dt>
            <dd>{String(cpf.taux_realisation)} %</dd>
          </>
        ) : null}
        {r.edof?.montant_attendu != null ? (
          <>
            <dt className="text-encre-3">Montant attendu (CDC)</dt>
            <dd>{euros(r.edof.montant_attendu)}</dd>
          </>
        ) : null}
        {cpf.numero_facture ? (
          <>
            <dt className="text-encre-3">Facture</dt>
            <dd>
              {String(cpf.numero_facture)} du {dateFr(String(cpf.date_facture))}
            </dd>
          </>
        ) : null}
        {cpf.date_reglement_prevue ? (
          <>
            <dt className="text-encre-3">Règlement prévu</dt>
            <dd>{dateFr(String(cpf.date_reglement_prevue))}</dd>
          </>
        ) : null}
        <dt className="text-encre-3">N° de sécurité sociale</dt>
        <dd>
          {cpf.titulaire_nir_renseigne ? "renseigné (chiffré, jamais affiché)" : "non renseigné (facultatif)"}
        </dd>
      </dl>
      <Bouton
        className="mt-3"
        variante="secondaire"
        taille="sm"
        enCours={enCours}
        onClick={() => enregistrer(v)}
      >
        Enregistrer les informations EDOF
      </Bouton>
    </Carte>
  );
}

function CarteBilan({
  r,
  enregistrer,
}: {
  r: DemandeFinancement;
  enregistrer: (b: Record<string, string>) => void;
}) {
  const b = r.demande.bilan as Record<string, string>;
  const [v, setV] = useState<Record<string, string>>({
    consentement_le: b.consentement_le ?? "",
    preliminaire_le: b.preliminaire_le ?? "",
    investigation_le: b.investigation_le ?? "",
    conclusions_le: b.conclusions_le ?? "",
    synthese_remise_le: b.synthese_remise_le ?? "",
    suivi_le: b.suivi_le ?? "",
  });
  return (
    <Carte className="p-4 sm:p-5">
      <h2 className="text-[15px] font-semibold">Bilan de compétences</h2>
      {r.bilan!.controle.map((c) => (
        <Alerte key={c} ton="danger">
          {c}
        </Alerte>
      ))}
      <ol className="mt-2 space-y-1 text-sm">
        {r.bilan!.phases.map((p, i) => (
          <li key={p.cle}>
            <strong>
              {i + 1}. {p.libelle}
            </strong>{" "}
            — <span className="text-encre-2">{p.detail}</span>
          </li>
        ))}
      </ol>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Champ
          libelle="Consentement recueilli le"
          type="date"
          value={v.consentement_le}
          onChange={(e) => setV({ ...v, consentement_le: e.target.value })}
        />
        <Champ
          libelle="Phase préliminaire le"
          type="date"
          value={v.preliminaire_le}
          onChange={(e) => setV({ ...v, preliminaire_le: e.target.value })}
        />
        <Champ
          libelle="Investigation (début) le"
          type="date"
          value={v.investigation_le}
          onChange={(e) => setV({ ...v, investigation_le: e.target.value })}
        />
        <Champ
          libelle="Conclusions le"
          type="date"
          value={v.conclusions_le}
          onChange={(e) => setV({ ...v, conclusions_le: e.target.value })}
        />
        <Champ
          libelle="Synthèse remise au bénéficiaire le"
          type="date"
          aide="Seule la date est conservée ici : le contenu reste au bénéficiaire."
          value={v.synthese_remise_le}
          onChange={(e) => setV({ ...v, synthese_remise_le: e.target.value })}
        />
        <Champ
          libelle="Entretien de suivi le"
          type="date"
          value={v.suivi_le}
          onChange={(e) => setV({ ...v, suivi_le: e.target.value })}
        />
      </div>
      <p className="mt-2 text-[13px] text-encre-2">
        Engagement de confidentialité :{" "}
        {r.engagement_bilan?.signe
          ? "signé par les trois parties"
          : "en attente de signatures (consultant, puis bénéficiaire)"}
        .
      </p>
      <Bouton className="mt-3" variante="secondaire" taille="sm" onClick={() => enregistrer(v)}>
        Enregistrer le déroulé
      </Bouton>
    </Carte>
  );
}

function ApercuCourriel({ id, fermer }: { id: string; fermer: () => void }) {
  const notifier = useNotifier();
  const requetes = useQueryClient();
  const q = useQuery({
    queryKey: ["financement-email", id],
    queryFn: () =>
      api.get<{
        destinataire: string;
        sujet: string;
        corps_html: string;
        pieces_jointes: string[];
        pret: boolean;
      }>(`/financements/${id}/email`),
  });
  const envoyer = useMutation({
    mutationFn: () =>
      api.post<{ statut: string; erreur: string }>(`/financements/${id}/email`, { confirmation: true }),
    onSuccess: async (r) => {
      await requetes.invalidateQueries({ queryKey: ["financement", id] });
      notifier(
        r.statut === "echec" ? "danger" : "succes",
        r.statut === "envoye"
          ? "E-mail envoyé."
          : r.statut === "journalise"
            ? "E-mail placé dans la boîte d'envoi (aucun SMTP configuré)."
            : `Échec : ${r.erreur}`,
      );
      fermer();
    },
    onError: (e) => notifier("danger", e.message),
  });
  return (
    <Modale ouverte fermer={fermer} titre="E-mail à l'interlocuteur du financeur" large>
      {!q.data ? (
        <Chargement />
      ) : (
        <>
          <p className="text-sm">
            À :{" "}
            <strong>{q.data.destinataire || "— adresse à renseigner dans la fiche du financeur —"}</strong>
          </p>
          <p className="text-sm">Objet : {q.data.sujet}</p>
          <p className="text-sm text-encre-2">
            Pièces jointes : {q.data.pieces_jointes.join(", ") || "aucune"}
          </p>
          <iframe
            title="Aperçu de l'e-mail"
            sandbox=""
            srcDoc={q.data.corps_html}
            className="mt-3 h-64 w-full rounded-md border border-trait bg-white"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Bouton variante="discret" onClick={fermer}>
              Annuler
            </Bouton>
            <Bouton
              variante="signal"
              disabled={!q.data.pret}
              enCours={envoyer.isPending}
              onClick={() => envoyer.mutate()}
            >
              Confirmer l'envoi
            </Bouton>
          </div>
        </>
      )}
    </Modale>
  );
}

// ——— Fiches financeurs (Organisme → Financeurs) ———

export function FichesFinanceurs() {
  useTitreDocument("Fiches financeurs");
  const notifier = useNotifier();
  const requetes = useQueryClient();
  const q = useQuery({
    queryKey: ["financeurs"],
    queryFn: () => api.get<FichesFinanceurs>("/admin/financeurs"),
  });
  const enregistrer = useMutation({
    mutationFn: ({ f, corps }: { f: string; corps: Record<string, unknown> }) =>
      api.put(`/admin/financeurs/${f}`, corps),
    onSuccess: async () => (
      await requetes.invalidateQueries({ queryKey: ["financeurs"] }),
      notifier("succes", "Fiche enregistrée.")
    ),
    onError: (e) => notifier("danger", e.message),
  });
  if (q.isPending) return <Chargement />;
  if (q.error) return <Alerte ton="danger">{q.error.message}</Alerte>;
  return (
    <>
      <TitrePage
        titre="Fiches financeurs"
        soustitre="Activation du module par financeur, canal de dépôt, délais, pièces exigées. Valeurs de départ vérifiées sur les sources citées ; ce qui n'a pas pu l'être est « à compléter »."
      />
      <div className="space-y-4">
        {q.data.fiches.map((f) => (
          <FicheFinanceur
            key={f.financeur}
            f={f}
            enregistrer={(corps) => enregistrer.mutate({ f: f.financeur, corps })}
            enCours={enregistrer.isPending}
          />
        ))}
      </div>
    </>
  );
}

type Fiche = FichesFinanceurs["fiches"][number];

function FicheFinanceur({
  f,
  enregistrer,
  enCours,
}: {
  f: Fiche;
  enregistrer: (c: Record<string, unknown>) => void;
  enCours: boolean;
}) {
  const [v, setV] = useState({
    portail_url: f.portail_url,
    email_interlocuteur: f.email_interlocuteur,
    canal_defaut: f.canal_defaut,
    ouverture: f.depot_ouverture_jours_avant?.toString() ?? "",
    limite: f.depot_limite_jours_avant?.toString() ?? "",
    justificatifs: f.justificatifs_jours_apres_fin?.toString() ?? "",
    relance: String(f.relance_jours),
  });
  const [correspondance, setCorrespondance] = useState<string | null>(null);
  const notifier = useNotifier();
  const requetes = useQueryClient();
  const [version, setVersion] = useState({
    millesime: String(new Date().getFullYear()),
    version: "",
    mis_en_ligne_le: "",
  });
  const televerser = useMutation({
    mutationFn: (fichier: File) =>
      api.fichier(`/admin/financeurs/${f.financeur}/formulaires`, fichier, version),
    onSuccess: async () => (
      await requetes.invalidateQueries({ queryKey: ["financeurs"] }),
      notifier("succes", "Formulaire téléversé : vérifiez la correspondance des champs.")
    ),
    onError: (e) => notifier("danger", e.message),
  });
  const nombre = (x: string) => (x.trim() === "" ? null : Number(x));
  const aCompleter = <span className="text-attente-encre">à compléter</span>;
  return (
    <Carte className="p-4 sm:p-5" data-testid={`fiche-${f.financeur}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[17px] font-semibold">{f.libelle}</h2>
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="size-4"
            checked={f.actif}
            onChange={(e) => enregistrer({ actif: e.target.checked })}
          />
          Module activé pour l'organisme
        </label>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Selecteur
          libelle="Canal par défaut"
          value={v.canal_defaut}
          onChange={(e) => setV({ ...v, canal_defaut: e.target.value as typeof v.canal_defaut })}
        >
          {f.canaux.map((c) => (
            <option key={c} value={c}>
              {LIBELLES_CANAL[c]}
            </option>
          ))}
        </Selecteur>
        <Champ
          libelle="Portail (https://…)"
          value={v.portail_url}
          onChange={(e) => setV({ ...v, portail_url: e.target.value })}
        />
        <Champ
          libelle="E-mail de l'interlocuteur"
          type="email"
          value={v.email_interlocuteur}
          onChange={(e) => setV({ ...v, email_interlocuteur: e.target.value })}
        />
        <Champ
          libelle="Dépôt au plus tôt (jours avant le début)"
          inputMode="numeric"
          placeholder="à compléter"
          value={v.ouverture}
          onChange={(e) => setV({ ...v, ouverture: e.target.value })}
        />
        <Champ
          libelle="Dépôt au plus tard (jours avant ; 0 = jour J)"
          inputMode="numeric"
          placeholder="à compléter"
          value={v.limite}
          onChange={(e) => setV({ ...v, limite: e.target.value })}
        />
        <Champ
          libelle="Justificatifs (jours après la fin)"
          inputMode="numeric"
          placeholder="à compléter"
          value={v.justificatifs}
          onChange={(e) => setV({ ...v, justificatifs: e.target.value })}
        />
        <Champ
          libelle="Relance après (jours)"
          inputMode="numeric"
          value={v.relance}
          onChange={(e) => setV({ ...v, relance: e.target.value })}
        />
      </div>
      <p className="mt-2 text-[13px] text-encre-2">
        Subrogation :{" "}
        {f.subrogation === null
          ? aCompleter
          : f.subrogation
            ? "oui"
            : "non — le financeur règle l'entreprise"}
        . Délais : {f.depot_limite_jours_avant === null ? aCompleter : "renseignés"}.
      </p>
      <Bouton
        className="mt-3"
        variante="secondaire"
        taille="sm"
        enCours={enCours}
        onClick={() =>
          enregistrer({
            canal_defaut: v.canal_defaut,
            portail_url: v.portail_url,
            email_interlocuteur: v.email_interlocuteur,
            depot_ouverture_jours_avant: nombre(v.ouverture),
            depot_limite_jours_avant: nombre(v.limite),
            justificatifs_jours_apres_fin: nombre(v.justificatifs),
            relance_jours: Number(v.relance) || 15,
          })
        }
      >
        Enregistrer la fiche
      </Bouton>
      <details className="mt-3">
        <summary className="cursor-pointer text-sm font-medium">
          Pièces exigées ({f.pieces.length}), règles et sources
        </summary>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
          {f.pieces.map((p) => (
            <li key={p.code}>
              {p.libelle} —{" "}
              <span className="text-encre-3">
                {p.phase === "avant" ? "avec la demande" : "après la formation"}
                {p.obligatoire ? "" : ", selon le cas"}
              </span>
            </li>
          ))}
        </ul>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-encre-2">
          {f.regles.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <p className="mt-2 text-[12px] text-encre-3">
          Sources :{" "}
          {f.sources.map((x, i) => (
            <a key={x.url + i} className="underline" href={x.url} target="_blank" rel="noopener noreferrer">
              {x.titre} (consulté le {dateFr(x.consulte_le)}){i < f.sources.length - 1 ? " ; " : ""}
            </a>
          ))}
        </p>
      </details>
      {f.canaux.includes("formulaire") && (
        <div className="mt-4 rounded-md border border-trait bg-papier-2 p-3">
          <h3 className="text-sm font-semibold">Formulaire officiel (versionné par millésime)</h3>
          {f.formulaire_actif?.perime && (
            <div className="mt-2">
              <Alerte ton="attention">
                Le formulaire enregistré date de {f.formulaire_actif.millesime} : vérifiez celui de l'année en
                cours.
              </Alerte>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <Champ
              libelle="Millésime"
              inputMode="numeric"
              className="w-28"
              value={version.millesime}
              onChange={(e) => setVersion({ ...version, millesime: e.target.value })}
            />
            <Champ
              libelle="Version"
              className="w-40"
              value={version.version}
              onChange={(e) => setVersion({ ...version, version: e.target.value })}
            />
            <Champ
              libelle="Mis en ligne le"
              type="date"
              value={version.mis_en_ligne_le}
              onChange={(e) => setVersion({ ...version, mis_en_ligne_le: e.target.value })}
            />
            <DepotFichier
              libelle="Téléverser le PDF"
              accept=".pdf"
              enCours={televerser.isPending}
              deposer={(x) => televerser.mutate(x)}
            />
          </div>
          {f.formulaires.length > 0 && (
            <ul className="mt-3 divide-y divide-trait text-sm">
              {f.formulaires.map((x) => (
                <li key={x.id} className="flex flex-wrap items-center gap-2 py-2">
                  <strong>{x.millesime}</strong> {x.version && <span>· {x.version}</span>}{" "}
                  <span className="text-encre-3">
                    · {x.nom_fichier} · {(x.champs as unknown[]).length} champ(s)
                  </span>
                  {x.actif ? <Etiquette ton="accent">En usage</Etiquette> : <Etiquette>Archivé</Etiquette>}
                  {x.perime && <Etiquette ton="attente">Année précédente</Etiquette>}
                  <a className="underline" href={`/api/admin/formulaires-financeur/${x.id}/pdf`}>
                    PDF
                  </a>
                  {(x.champs as unknown[]).length > 0 && (
                    <Bouton taille="sm" variante="discret" onClick={() => setCorrespondance(x.id)}>
                      Correspondance des champs
                    </Bouton>
                  )}
                </li>
              ))}
            </ul>
          )}
          {correspondance && (
            <EditeurCorrespondance
              formulaire={f.formulaires.find((x) => x.id === correspondance)!}
              fermer={() => setCorrespondance(null)}
            />
          )}
        </div>
      )}
    </Carte>
  );
}

const SOURCES = [
  ["", "— laisser vide (à remplir à la main) —"],
  ["demande_numero_portail", "N° de dossier chez le financeur"],
  ["of_nom", "Organisme — raison sociale"],
  ["of_nda_numero", "Organisme — n° de déclaration d'activité"],
  ["of_siret", "Organisme — SIRET"],
  ["of_siren", "Organisme — SIREN"],
  ["of_adresse_rue", "Organisme — adresse (rue)"],
  ["of_adresse_cp", "Organisme — code postal"],
  ["of_adresse_ville", "Organisme — ville"],
  ["of_telephone", "Organisme — téléphone"],
  ["of_email", "Organisme — e-mail"],
  ["formation_titre_1", "Formation — intitulé (ligne 1)"],
  ["formation_titre_2", "Formation — intitulé (ligne 2)"],
  ["formation_duree", "Formation — durée (h)"],
  ["formation_date_debut", "Formation — début"],
  ["formation_date_fin", "Formation — fin"],
  ["formation_montant", "Formation — montant HT"],
  ["formation_lieu_adresse", "Lieu — adresse complète"],
  ["formation_lieu_cp", "Lieu — code postal"],
  ["formation_lieu_ville", "Lieu — ville"],
  ["entreprise_nom", "Entreprise — raison sociale"],
  ["entreprise_siren", "Entreprise — SIREN"],
  ["entreprise_adresse_rue", "Entreprise — rue"],
  ["entreprise_adresse_cp", "Entreprise — code postal"],
  ["entreprise_adresse_ville", "Entreprise — ville"],
  ["entreprise_telephone", "Entreprise — téléphone"],
  ["entreprise_mail", "Entreprise — e-mail"],
  ["stagiaire_nom", "Stagiaire — nom"],
  ["stagiaire_prenom", "Stagiaire — prénom"],
  ["stagiaire_email", "Stagiaire — e-mail"],
  ["=coche", "Case cochée"],
] as const;

function EditeurCorrespondance({
  formulaire,
  fermer,
}: {
  formulaire: Fiche["formulaires"][number];
  fermer: () => void;
}) {
  const notifier = useNotifier();
  const requetes = useQueryClient();
  const [c, setC] = useState<Record<string, string>>(formulaire.correspondance as Record<string, string>);
  const enregistrer = useMutation({
    mutationFn: () =>
      api.put(`/admin/formulaires-financeur/${formulaire.id}/correspondance`, { correspondance: c }),
    onSuccess: async () => (
      await requetes.invalidateQueries({ queryKey: ["financeurs"] }),
      notifier("succes", "Correspondance enregistrée."),
      fermer()
    ),
    onError: (e) => notifier("danger", e.message),
  });
  return (
    <Modale
      ouverte
      fermer={fermer}
      titre={`Correspondance des champs — formulaire ${formulaire.millesime}`}
      large
    >
      <p className="text-sm text-encre-2">
        Proposition automatique d'après les noms des champs : relisez-la. Une valeur fixe s'écrit précédée de
        « = ».
      </p>
      <div className="mt-3 max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {(formulaire.champs as Array<{ nom: string; type: string }>).map((ch) => {
          const val = c[ch.nom] ?? "";
          const connue = SOURCES.some(([k]) => k === val);
          return (
            <div key={ch.nom} className="grid items-end gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
              <span className="truncate font-mono text-[13px]" title={ch.nom}>
                {ch.nom}
                {ch.type === "case" ? " (case)" : ""}
              </span>
              <div className="flex gap-2">
                <Selecteur
                  libelle=""
                  aria-label={`Donnée pour ${ch.nom}`}
                  value={connue ? val : "__fixe"}
                  onChange={(e) =>
                    setC({ ...c, [ch.nom]: e.target.value === "__fixe" ? "=" : e.target.value })
                  }
                  className="flex-1"
                >
                  {SOURCES.map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                  <option value="__fixe">Valeur fixe…</option>
                </Selecteur>
                {!connue && (
                  <Champ
                    libelle=""
                    aria-label={`Valeur fixe pour ${ch.nom}`}
                    value={val.replace(/^=/, "")}
                    onChange={(e) => setC({ ...c, [ch.nom]: `=${e.target.value}` })}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Bouton variante="discret" onClick={fermer}>
          Annuler
        </Bouton>
        <Bouton variante="signal" enCours={enregistrer.isPending} onClick={() => enregistrer.mutate()}>
          Enregistrer
        </Bouton>
      </div>
    </Modale>
  );
}
